import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createRoleKey } from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';

const EMPTY_FORM = { key: '', name: '', description: '' };

/**
 * EAA-1817: "Tạo vai trò chuẩn" — the empty-state CTA when the ecosystem has
 * no `role_key` vocabulary yet (frame 54's second empty card). Inline rather
 * than a modal: the form is two short fields plus an optional description,
 * and the empty state that hosts it already reads as a dedicated surface.
 *
 * Gated on `console.rolemap.manage` by the caller (`RoleEditorCard`/
 * `EmptyStates.tsx`) — role keys are ecosystem-wide vocabulary, the same
 * population that manages default-scope RoleMap links.
 */
export function useRoleKeyCreateForm(apiBase: string, token: string | null) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState<string | null>(null);

    const mutation = useMutation({
        mutationFn: () =>
            createRoleKey(apiBase, token as string, {
                key: form.key.trim(),
                name: form.name.trim(),
                description: form.description.trim() || undefined,
            }),
        onSuccess: () => {
            setError(null);
            setForm(EMPTY_FORM);
            setOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['role-keys'] });
            void queryClient.invalidateQueries({ queryKey: ['role-map-coverage'] });
        },
        onError: (err: unknown) => {
            setError(
                err instanceof OrgAdminApiError
                    ? err.message
                    : t('console.roleCoverage.errors.createRoleKeyFailed')
            );
        },
    });

    function onSubmit(event: FormEvent) {
        event.preventDefault();
        if (!form.key.trim() || !form.name.trim()) return;
        mutation.mutate();
    }

    return {
        open,
        setOpen,
        form,
        setForm,
        error,
        onSubmit,
        pending: mutation.isPending,
    };
}

export type RoleKeyCreateFormVM = ReturnType<typeof useRoleKeyCreateForm>;
